/**
 * Core runtime functionality.
 * Responsible for initializing runtime and running workflows in the correct order.
 */

import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import isEqual from 'fast-deep-equal';
import {
  catchError,
  combineLatest,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  groupBy,
  last,
  map,
  mergeMap,
  Observable,
  of,
  pairwise,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

import {
  AccountsTableStaleness,
  runtimeActions,
  runtimeSelectors,
  RuntimeState,
} from '@blaze-runtime/engine-data-access';
import { CanvasElement, CanvasElementId } from '@blaze-runtime/engine-domain';
import { type ConnectionChain, getChainByChainId, getChainId } from '@blaze-runtime/engine-domain';
import {
  appStateActions,
  blazeActions,
  ProjectContextFacade,
  projectContextSelectors,
} from '@blaze-shared/app-state-data-access';
import { sentryLog } from '@blaze-shared/logger-util';
import {
  getSubscriptionIdFromWebsocketMessage,
  getWorkflowIdToTriggerFromSubscriptionId,
  getWorkflowIdToTriggerFromWebsocketMessage,
  getWorkflowSourceFromWebsocketMessage,
  isSubscriptionMessage,
  LocalStorageService,
  StorageKey,
} from '@blaze-shared/platform-data-access';
import { DatasetRowsService, SchemaService } from '@blaze-shared/schema-data-access';
import {
  DatasetRow,
  DEFAULT_ACCOUNT_VALUE,
  isVarAccount,
  VarAccount,
} from '@blaze-shared/schema-domain';
import { isDeepEqual, isNullish } from '@blaze-shared/shared-util';
import { VariablesFacade } from '@blaze-shared/variables-data-access';
import { WorkflowsApiService } from '@blaze-shared/workflow-data-access';
import {
  Connection,
  isActionWorkflow,
  isServerOnlyWorkflow,
  isStandaloneWorkflow,
  RuntimeElement,
  Workflow,
  WorkflowPhase,
} from '@blaze-shared/workflow-domain';

import { fromErrors } from '..';
import { findRelatedNamespaces } from '../../canvas-elements/element-model/namespaces';
import { RuntimeService } from '../../services/runtime.service';
import { getConnectionsTo, getUiRuntimeElement } from '../../utils/runtime';
import { getElements } from '../elements/elements.selectors';

import { RuntimeFacade } from './runtime.facade';

@Injectable({ providedIn: 'root' })
export class RuntimeCoreEffects {
  private readonly accountsTableStaleness = inject(AccountsTableStaleness);
  private readonly actions$ = inject(Actions);
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly runtimeService = inject(RuntimeService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store<RuntimeState>);
  private readonly variablesFacade = inject(VariablesFacade);
  private readonly workflowsApiService = inject(WorkflowsApiService);

  private runtimeStarted: boolean | undefined = undefined;

  /**
   * Step 1a: Initialize page runtime information when accounts information has not yet been fetched.
   * This includes information such as external accounts and whether an accounts dropdown is present on
   * the page. This has to be set before anything loads on the page.
   */
  setInitialRuntimeParamsAccountsNotFound$ = createEffect(() =>
    this.actions$.pipe(
      ofType(blazeActions.waitForPageAndStartRuntime),
      // BL-4780: it's possible to click so quickly that distinctUntilChanged is never triggered and runtime is never
      // started. Because of this, we can't use that operator here.
      filter(({ projectId, pageId }) => !!(projectId && pageId)),
      concatLatestFrom(() => [
        this.store.select(projectContextSelectors.selectExternalAccounts),
        this.store.select(runtimeSelectors.selectIsCustomerPortal),
      ]),
      filter(([_, accounts]) => !accounts),
      tap(() => {
        // Dataset operations are not dependent on the selected account. In order to ensure that we don't fetch data with
        // the account selected in another project, we must clear the data and let the data be set again.
        // Do this only for the logged in user; when previewing other users we still need access to VarAccount in order
        // to show proper data in Preview mode.
        if (!this.projectContextFacade.isPreviewOfAnotherUser) {
          this.store.dispatch(appStateActions.deleteVarAccount());
        }
      }),
      switchMap(([{ projectId, pageId }, _accounts, isCustomerPortal]) => {
        // Ensure that account is set when navigating to a different page.
        const schema$ = isCustomerPortal
          ? this.schemaService.obtainAccountsSchema(projectId)
          : of(null);
        return schema$.pipe(
          concatMap((schema) => {
            // Get latest accounts dropdown-related information
            return combineLatest({
              accounts: schema
                ? this.datasetRowsService.get({ datasetId: schema.id, includeRowId: false })
                : of([]),
              accountsDatasetId: of(schema?.id || ''),
              accountsSchema: of(schema),
              hasAccountSelector: this.runtimeService.isPageWithAccountSelector(
                pageId,
                isCustomerPortal,
              ),
            }).pipe(first());
          }),
          tap(({ accounts, accountsDatasetId, hasAccountSelector }) => {
            this.accountsTableStaleness.markStale(false);
            const currSetAccount = this.projectContextFacade.varAccount ?? {
              ...DEFAULT_ACCOUNT_VALUE,
            };

            this.initExternalAccountVar(projectId, accounts, currSetAccount, hasAccountSelector);

            if (accountsDatasetId) {
              this.store.dispatch(
                runtimeActions.observeExternalAccounts({ datasetId: accountsDatasetId }),
              );
            }
          }),
          concatMap(({ hasAccountSelector }) => {
            return [
              blazeActions.setRuntimePage({
                pageId,
                projectId,
                runtimePage: { hasAccountSelector },
              }),
              blazeActions.initSettings(),
            ];
          }),
        );
      }),
    ),
  );

  /**
   * Step 1b: Initialize external accounts and listen on changes to the list of the available accounts.
   */
  observeExternalAccounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.observeExternalAccounts),
      switchMap((externalAccount) => {
        return this.datasetRowsService
          .observeDatasetAndRelatedChanged(externalAccount.datasetId)
          .pipe(
            switchMap(() => {
              return this.datasetRowsService.get({
                datasetId: externalAccount.datasetId,
                includeRowId: false,
              });
            }),
            map((rows) => {
              return appStateActions.setExternalAccounts({ accounts: rows });
            }),
          );
      }),
    ),
  );

  /**
   * Step 1c: Keep track of latest projectId and pageId.
   */
  persistLastUsedProjectAndPageOnStartRuntime$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(blazeActions.waitForPageAndStartRuntime),
        tap(({ projectId, pageId }) => {
          this.localStorageService.set(StorageKey.LastUsedProjectId, projectId);
          this.localStorageService.set(StorageKey.LastUsedPageId, pageId);
        }),
      ),
    { dispatch: false },
  );

  /**
   * Step 1c: Initialize page runtime information when accounts info has already been fetched.
   * This includes information such as external accounts and whether an accounts dropdown is present on
   * the page. This has to be set before anything loads on the page.
   */
  setInitialRuntimeAccountsFound$ = createEffect(() =>
    this.actions$.pipe(
      ofType(blazeActions.waitForPageAndStartRuntime),
      // BL-4780: it's possible to click so quickly that distinctUntilChanged is never triggered and runtime is never
      // started. Because of this, we can't use that operator here.
      filter(({ projectId, pageId }) => !!(projectId && pageId)),
      concatLatestFrom(() => [
        this.store.select(projectContextSelectors.selectExternalAccounts),
        this.store.select(runtimeSelectors.selectRuntimePages),
      ]),
      filter(([_, accounts]) => !!accounts),
      switchMap(([{ projectId, pageId }, accounts, pageInfos]) => {
        return this.refreshExternalAccounts(accounts).pipe(
          map(({ currentVarAccount, externalAccounts }) => {
            return {
              accounts: externalAccounts,
              currentVarAccount,
              pageId,
              pageInfos,
              projectId,
            };
          }),
        );
      }),
      tap(({ accounts, currentVarAccount, pageId, pageInfos, projectId }) => {
        // We can only utilize pageInfos to initialize external account if hasAccountSelector information has been set.
        // Otherwise we risk that the data on the page will unnecessairly refresh (when account selector is found)
        // and data will either disappear or blink.
        if (pageInfos[pageId] && accounts && !isNullish(pageInfos[pageId]?.hasAccountSelector)) {
          this.initExternalAccountVar(
            projectId,
            accounts,
            currentVarAccount,
            !!pageInfos[pageId]?.hasAccountSelector,
          );
        }
      }),
      switchMap(({ accounts, currentVarAccount, pageId, projectId }) => {
        // Ensure that account is set when navigating to a different page.
        return this.runtimeService.isPageWithAccountSelector(pageId).pipe(
          map((hasAccountSelector) => {
            if (!accounts) {
              throw new Error('Expected accounts to be defined');
            }

            this.initExternalAccountVar(projectId, accounts, currentVarAccount, hasAccountSelector);

            return blazeActions.setRuntimePage({
              pageId,
              projectId,
              runtimePage: { hasAccountSelector },
            });
          }),
        );
      }),
    ),
  );

  /**
   * Step 3: Find and initiate workflows which should run without UI interaction.
   * Finds workflows which as a start node have either GetWorkflowNode or ApiCallWorkflowNode. We assume these
   * workflows should run when the runtime starts, if those workflows are not a target of any connection.
   */
  autostartDataWorkflows$ = createEffect(() =>
    this.actions$.pipe(
      ofType(blazeActions.startRuntime),
      concatLatestFrom(() => [
        this.store.select(runtimeSelectors.selectPageId),
        this.store.select(runtimeSelectors.selectConnections),
        this.store.select(runtimeSelectors.selectWorkflows),
        this.store.select(runtimeSelectors.selectInitializedUiElements),
      ]),
      mergeMap(([_, currentNamespace, allConnections, workflows, initializedUiElements]) => {
        console.log(`Start runtime for page:`, currentNamespace);
        // Start only connections associated with workflows which haven't been started by UI elements.
        allConnections = allConnections.filter(
          (conn) => initializedUiElements[conn.target?.id] === undefined,
        );
        const connectionsToStart = this.runtimeService.getAutostartConnections(
          allConnections,
          workflows,
        );
        const actions: Action[] = [];

        connectionsToStart.forEach((connections, ns) => {
          if (ns === currentNamespace) {
            actions.push(runtimeActions.queueConnections({ ns, connections }));
          }
        });

        return actions;
      }),
    ),
  );

  /**
   * Step 4: Request to queue actions received. For each received connection, build a connection chain. The chain is
   * a series of steps that need to execute before the next connection can be run.
   * Each connection chain must complete before another one can be started. We achieve this by adding a complete
   * connection chain to the queue and hanlding each connection one by one. New connection chains are appended to
   * the end of the queue.
   */
  queueConnections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.queueConnections),
      concatLatestFrom(() => [
        this.store.select(runtimeSelectors.selectWorkflows),
        this.store.select(getElements),
        this.store.select(runtimeSelectors.selectPageId),
      ]),
      mergeMap(([{ connections, ns }, workflows, elements, pageId]) => {
        const queueConnectionChainsActions: Action[] = [];
        connections.forEach((connection) => {
          const workflow = workflows.find(
            (wf) => wf.id === connection.target.id || wf.id === connection.source.id,
          );
          // Server-only run workflows should never under any circumstances be queued
          if (!isServerOnlyWorkflow(workflow)) {
            const relatedNamespaces = findRelatedNamespaces(elements, pageId);
            // We need to find proper namespace in case workflows are triggered from a component found in the nav header.
            ns = this.runtimeService.getConnectionNamespace(connection, ns, relatedNamespaces);
            const connectionsChain = this.runtimeService.createConnectionChain(connection);
            queueConnectionChainsActions.push(
              runtimeActions.queueConnectionChain({ ns, connectionChain: [...connectionsChain] }),
            );
          }
        });
        return queueConnectionChainsActions;
      }),
    ),
  );

  /**
   * Step 5: Checks if there are any connections in the queue that need to be run. If yes, it fetches appropriate
   * input data for the Connection and triggers runtime event to apply appropriate transformations.
   */
  dispatchEventForConnection$ = createEffect(() =>
    combineLatest({
      // Get chains of connections waiting to be executed before and after store change so that we can properly
      // deduce which connection should be executed next.
      chains: this.store.select(runtimeSelectors.selectConnectionChains).pipe(
        distinctUntilChanged((prevChainsDict, currChainsDict) =>
          isEqual(prevChainsDict, currChainsDict),
        ),
        pairwise(),
      ),
      isRunning: this.store.select(runtimeSelectors.selectIsRunning),
    }).pipe(
      concatLatestFrom(() => [
        this.store.select(runtimeSelectors.selectNamespace),
        this.store.select(runtimeSelectors.selectPageId),
        this.store.select(getElements),
      ]),
      tap(([{ isRunning }]) => this.updateIsRuntimeStarted(isRunning)),
      filter(([{ isRunning }, ns]) => !!ns && isRunning),
      mergeMap(([{ chains }, ns, pageId, elements]) => {
        const { namespacesToStart, relatedNamespaces } = this.findNamespacesToStart(
          ns,
          pageId,
          elements,
        );
        return this.getChainsToStart(chains, namespacesToStart, relatedNamespaces);
      }),
      // Create a batch of connections that need to be run. This map() will be executed separately for each of
      // the elements in the array returned by getChainsToStart.
      map(({ prevChains, currChains, ns, relatedNamespaces }) => {
        const connectionsToStart: Connection[] = this.getConnectionsToStart(prevChains, currChains);
        return { connectionsToStart, ns, relatedNamespaces };
      }),
      filter(({ connectionsToStart }) => !!connectionsToStart?.length),
      mergeMap(({ connectionsToStart, ns, relatedNamespaces }) => {
        const runtimeEventActions: Action[] = [];
        for (const connection of connectionsToStart) {
          const data = this.runtimeService.getDataForElement(
            connection.source,
            connection.eventType,
          );
          const schema = this.runtimeService.getSchemaForRuntimeElement(
            connection.source,
            connection.eventType,
          );
          const eventContext = this.runtimeService.getEventContextForElement(
            connection.source,
            connection.eventType,
          );
          // We need to find proper namespace in case workflows are triggered from a component found in the nav header.
          ns = this.runtimeService.getConnectionNamespace(connection, ns, relatedNamespaces);
          runtimeEventActions.push(
            runtimeActions.runtimeEvent({ event: { connection, data, ns, schema, eventContext } }),
          );
        }
        return runtimeEventActions;
      }),
    ),
  );

  /**
   * Step 6: No more connections need to be executed at the time. Complete the cycle.
   */
  connectionsCompleteOnPrevStepCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.connectionStepComplete),
      concatLatestFrom(() => this.store.select(runtimeSelectors.selectConnectionChains)),
      filter(([{ event }, allChains]) => {
        // No more connections let to run in the given namespace
        return allChains[event.ns]?.length === 0;
      }),
      map(() => runtimeActions.connectionsComplete()),
    ),
  );

  /**
   * Re-run workflows after clearing app data.
   */
  runWorkflowsAfterClearingOutputs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.clearOutputsAndSchema),
      concatLatestFrom(() => [
        this.store.select(runtimeSelectors.selectNamespace),
        this.store.select(getElements),
        this.store.select(runtimeSelectors.selectConnections),
        this.store.select(runtimeSelectors.selectWorkflows),
        this.store.select(runtimeSelectors.selectProjectId),
      ]),
      mergeMap(([_, namespace, elements, connections, workflows, projectId]) => {
        // Fetch newest stored variables before proceeding with queuing the connections
        const currAccountName = this.projectContextFacade.varAccount?.Name;
        return this.variablesFacade.initVariables(projectId, currAccountName).pipe(
          map(() => {
            return { namespace, elements, connections, workflows };
          }),
        );
      }),
      mergeMap(({ workflows, connections, elements, namespace }) => {
        // Find related namespaces
        const namespaces = findRelatedNamespaces(elements, namespace);
        const connectionsToStart = this.runtimeService.getAutostartConnections(
          connections,
          workflows,
        );
        const actions: Action[] = [];
        connectionsToStart.forEach((connToStart, ns) => {
          // Include connections which are a part of the main page (including nested layouts, such as navigation bars)
          if (namespaces.includes(ns)) {
            actions.push(runtimeActions.queueConnections({ ns, connections: connToStart }));
          }
        });
        return actions;
      }),
    ),
  );

  /**
   * Runs autostartable workflows in the specified namespace. This is used to auto-run workflows in the newly opened
   * dialog, drawer, etc.
   */
  runWorkflowsInNewNamespace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.autostartNamespace),
      concatLatestFrom(() => [
        this.store.select(runtimeSelectors.selectConnections),
        this.store.select(runtimeSelectors.selectWorkflows),
      ]),
      mergeMap(([{ ns }, connections, workflows]) => {
        const nsConnections = connections.filter((connection) => connection?.id.startsWith(ns));
        const connectionsToStart = this.runtimeService.getAutostartConnections(
          nsConnections,
          workflows,
        );
        const actions: Action[] = [];

        connectionsToStart.forEach((connToStart, ns) => {
          actions.push(runtimeActions.queueConnections({ ns, connections: connToStart }));
        });

        return actions;
      }),
    ),
  );

  /**
   * Detects dataset change and finds all workflows which rely on that dataset. For each workflow, gets
   * connections and adds them to the execution queue.
   */
  runWorkflowsOnDatasetChanges$ = createEffect(() =>
    this.datasetRowsService.observeDatasetsChanged().pipe(
      concatLatestFrom(() => [
        this.store.select(runtimeSelectors.selectPageId),
        this.store.select(runtimeSelectors.selectNamespace),
      ]),
      mergeMap(([datasetIds, currentPageId, currentNamespace]) => {
        // Get workflows which are using the given dataset
        const workflowsToRefresh = datasetIds.flatMap((datasetId) =>
          this.getWorkflowsToRefreshOnDatasetChanges(datasetId),
        );

        const uniqueWorkflowsToRefresh: Workflow[] = [...new Set(workflowsToRefresh)];
        const connectionsToRefresh = this.runtimeService.getDataChangedConnections(
          uniqueWorkflowsToRefresh,
          true,
        );

        const actions: Action[] = [];
        connectionsToRefresh.forEach((connections, ns) => {
          // When dataset changes it should trigger workflow refresh in two places:
          // 1. Dialog on which the workflow has been executed (dialog can possibly display changed data without closing)
          // 2. Underlying page which shows the modified data right after the dialog closes after performing an action
          if (ns === currentNamespace || ns === currentPageId) {
            actions.push(
              runtimeActions.queueConnections({ ns, connections, skipNamespaceChange: true }),
            );
          }
        });

        return actions;
      }),
    ),
  );

  /**
   * Listens on workflow changes. If a given workflow is edited or added, it re-runs that workflow.
   */
  runWorkflowsOnWorkflowsChange$ = createEffect(() =>
    this.store.select(runtimeSelectors.selectWorkflows).pipe(
      distinctUntilChanged(isDeepEqual),
      pairwise(),
      concatLatestFrom(() => [
        this.store.select(runtimeSelectors.selectIsRunning),
        this.store.select(runtimeSelectors.selectConnections),
      ]),
      // Run workflow only if we're editing existing or adding new workflow and not removing one.
      filter(([[prevWorkflows, currWorkflows], isRunning, connections]) => {
        return isRunning && !!connections?.length && currWorkflows?.length >= prevWorkflows?.length;
      }),
      switchMap(([[prevWorkflows, currWorkflows], _isRunning, connections]) => {
        const workflowIds = this.getNewOrEditedWorkflowIds(prevWorkflows, currWorkflows);
        const actions: Action[] = [];
        workflowIds.forEach((workflowId) => {
          /** Ignore preventAutostart if workflow is not an action and doesn't have input or output connections. */
          const ignorePreventAutostart =
            !isActionWorkflow(connections, workflowId) &&
            !isStandaloneWorkflow(connections, workflowId);
          actions.push(runtimeActions.executeWorkflow({ workflowId, ignorePreventAutostart }));
        });
        return actions;
      }),
    ),
  );

  /**
   * Run workflow connected to a UI element in order to initialize that element.
   */
  runWorkflowOnExecuteUiWorkflowAction_execute$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.executeUiWorkflow),
      concatLatestFrom(() => [
        this.store.select(runtimeSelectors.selectConnections),
        this.store.select(runtimeSelectors.selectWorkflows),
      ]),
      map(([{ elementId }, connections, workflows]) => {
        const target = getUiRuntimeElement(elementId);
        const connectionsToUiElement = getConnectionsTo(connections, target);
        const workflowsIds = connectionsToUiElement
          .filter((c) => c.source.layerType === 'Workflow')
          .map((c) => c.source?.id)
          .filter(Boolean);
        const workflowInfos = workflows
          .filter((w) => workflowsIds.includes(w.id))
          .map((w) => ({
            workflow: w,
            hasInputConnections: this.runtimeService.hasInputConnections(w, connections),
          }));

        return { elementId, workflowInfos };
      }),
      filter(({ elementId, workflowInfos }) => !!(elementId && workflowInfos?.length > 0)),
      mergeMap(({ elementId, workflowInfos }) => {
        const actions: Action[] = [];

        for (const { workflow, hasInputConnections } of workflowInfos) {
          const firstNode = workflow.nodes.find((node) => node.id === workflow.startNodeId);
          // We don't want to execute workflows which have input connections to avoid running
          // them multiple times in a row (BL-2086)
          if (
            (hasInputConnections && !workflow.allowNullInput) ||
            !this.runtimeService.isAutostartableNode(firstNode)
          ) {
            actions.push(runtimeActions.uiWorkflowExecuted({ elementId }));
          } else {
            actions.push(runtimeActions.executeWorkflow({ workflowId: workflow?.id }));
          }
        }

        return actions;
      }),
    ),
  );

  /**
   * UI element which is trying to execute a workflow doesn't have any connections leading to it.
   * Dispatch action notifying that the workflow was executed so that the UI element
   * can properly render.
   */
  runWorkflowOnExecuteUiWorkflowAction_markAsExecuted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.executeUiWorkflow),
      filter(({ elementId }) => {
        const target: RuntimeElement = { id: elementId, layerType: 'Ui' };
        const connections = this.runtimeFacade.getConnectionByTarget(target);
        return !connections && !this.runtimeService.isElementInitialized(elementId);
      }),
      map(({ elementId }) => {
        return runtimeActions.uiWorkflowExecuted({ elementId });
      }),
    ),
  );

  /**
   * Runs specified workflow.
   */
  runWorkflowOnExecuteWorkflowAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.executeWorkflow),
      concatLatestFrom(() => this.store.select(runtimeSelectors.selectConnections)),
      mergeMap(([{ workflowId, ignorePreventAutostart, skipNamespaceChange }, connections]) => {
        console.log(`Execute workflow: ${workflowId}`);
        const workflow = this.runtimeFacade.getWorkflowById(workflowId);
        const actions: Action[] = [];

        if (workflow && !isServerOnlyWorkflow(workflow)) {
          // Find connections associated with the given workflow and run them.
          const connectionsToStart = this.runtimeService.getAutostartConnections(
            connections,
            [workflow],
            true,
            ignorePreventAutostart,
          );
          connectionsToStart.forEach((namespaceConnections, ns) => {
            actions.push(
              runtimeActions.queueConnections({
                ns,
                connections: namespaceConnections,
                skipNamespaceChange,
              }),
            );
            const pendingUiActions = this.showLoadingIndicatorOnConnectedElements(
              namespaceConnections,
              workflowId,
            );
            actions.push(...pendingUiActions);
          });
        }
        return actions;
      }),
    ),
  );

  /**
   * Runs workflows when a websocket message informs that source data of the workflow
   * (data returned by first GET node) has changed.
   */
  runWorkflowOnWebsocketMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(runtimeActions.subscribeToWebSocketWfRefresh),
      filter(({ socketConfig, subscriptionIds }) => {
        return !!socketConfig?.websocket && !!subscriptionIds?.length;
      }),
      mergeMap(({ socketConfig, subscriptionIds }) => {
        return socketConfig.websocket.pipe(
          filter((message) => {
            // BL-9335: ensure each websocket subscription handles only its own messages.
            const msgSubscriptionId = getSubscriptionIdFromWebsocketMessage(message);
            return msgSubscriptionId ? subscriptionIds.includes(msgSubscriptionId) : true;
          }),
          catchError((error) => {
            // Websocket connection errored out. Rerun workflows in order to reestablish websocket
            // subscriptions.
            console.log(
              'Workflow refresh socket connection error. Attempting to restore subscriptions',
            );
            let workflowIds: string[] = subscriptionIds
              .map((subId) => getWorkflowIdToTriggerFromSubscriptionId(subId))
              .filter(Boolean);
            workflowIds = Array.from(new Set(workflowIds));
            for (const workflowId of workflowIds) {
              this.store.dispatch(
                runtimeActions.executeWorkflow({ workflowId, ignorePreventAutostart: false }),
              );
            }
            return throwError(() => error);
          }),
        );
      }),
      filter((message) => isSubscriptionMessage(message)),
      map((message) => {
        return {
          sourceWfId: getWorkflowSourceFromWebsocketMessage(message),
          wfIdToTrigger: getWorkflowIdToTriggerFromWebsocketMessage(message),
        };
      }),
      mergeMap(({ sourceWfId, wfIdToTrigger }) => {
        // BL-10598 Wait on the triggering workflow to complete before parsing messages
        if (!sourceWfId) {
          return of(wfIdToTrigger);
        }
        return this.store.select(runtimeSelectors.selectWorkflowsStatusById(sourceWfId)).pipe(
          filter((status) => status?.phase !== WorkflowPhase.Running),
          first(),
          map(() => wfIdToTrigger),
        );
      }),
      filter((wfIdToTrigger): wfIdToTrigger is string => !!wfIdToTrigger),
      // BL-9781: When update happens in a ForEach node, multiple websocket messages are sent.
      // We need to debounce them to avoid running the workflow multiple times.
      groupBy((wfIdToTrigger) => wfIdToTrigger, {
        duration(grouped$) {
          return grouped$.pipe(debounceTime(300));
        },
      }),
      mergeMap((grouped$) => {
        return grouped$.pipe(
          last(),
          concatLatestFrom((wfIdToTrigger) => [
            this.store.select(runtimeSelectors.selectConnections),
            this.store.select(runtimeSelectors.selectIsRunning),
            this.store.select(getElements),
            this.store.select(runtimeSelectors.selectNamespace),
            this.store.select(runtimeSelectors.selectPageId),
            this.store.select(runtimeSelectors.selectWorkflowById(wfIdToTrigger)),
          ]),
          map(
            ([
              wfIdToTrigger,
              connections,
              isRunning,
              elements,
              currentNs,
              currPageId,
              workflow,
            ]) => {
              return {
                connections,
                currentNs,
                currPageId,
                elements,
                isRunning,
                wfIdToTrigger,
                workflow,
              };
            },
          ),
          filter(
            ({
              connections,
              currentNs,
              currPageId,
              elements,
              isRunning,
              wfIdToTrigger,
              workflow,
            }) => {
              // Only workflows within the current namespace are allowed to run on websocket message
              if (!workflow?.pageId) {
                return false;
              }
              const wfPageId = workflow.pageId;
              const relatedNamespaces = findRelatedNamespaces(elements, wfPageId).filter(
                (pid) => pid !== wfPageId,
              );
              if (
                wfPageId !== currentNs &&
                wfPageId !== currPageId && // BL-9661: allow workflows on dialog's parent page to refresh
                !relatedNamespaces.includes(wfPageId)
              ) {
                return false;
              }
              return !!wfIdToTrigger && isRunning && !!connections?.length;
            },
          ),
          switchMap(({ connections, wfIdToTrigger }) => {
            /** Ignore preventAutostart if workflow is not an action or has input or output connections. */
            const ignorePreventAutostart =
              !isActionWorkflow(connections, wfIdToTrigger) &&
              !isStandaloneWorkflow(connections, wfIdToTrigger);
            console.log(`Source data of workflow ${wfIdToTrigger} has changed. Rerunning workflow`);
            return [
              runtimeActions.executeWorkflow({
                ignorePreventAutostart,
                skipNamespaceChange: true,
                workflowId: wfIdToTrigger,
              }),
            ];
          }),
        );
      }),
    ),
  );

  /**
   * Handle errors
   */
  onConnectionError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromErrors.runtimeError),
        tap(({ errorMessage, workflowId, ns, origin, nodeId }) => {
          nodeId = nodeId || '';
          console.error(
            `Unable to complete connection. Error detected:`,
            errorMessage,
            `namespace:`,
            ns,
            `workflowId:`,
            workflowId,
            `origin`,
            origin,
            `nodeId`,
            nodeId,
          );
          const workflow = this.runtimeFacade.getWorkflowById(workflowId || '');
          if (workflow?.logExecution) {
            const log: any = {
              Node: nodeId,
              'Origin of error': origin,
              Namespace: ns,
            };
            if (workflowId) {
              this.workflowsApiService
                .postLog(workflowId, nodeId, `Runtime error: ${errorMessage}`, log)
                .subscribe();
            } else {
              console.warn(`Unable to log to server. Workflow ID not specified.`);
            }
          }
          this.runtimeService.showWorkflowError({ errorMessage, nodeId, workflow });
        }),
      ),
    { dispatch: false },
  );

  private findNamespacesToStart(
    currentNs: string,
    currentPageId: string,
    elements: CanvasElement[],
  ): {
    namespacesToStart: string[];
    relatedNamespaces: string[];
  } {
    // Figure out the namespaces to start
    // If current namespace is hosted within the current page (for example it points to a navigation bar in the current page)
    // start the connection for the whole page (root + hosted inside of it) (BL-3579).
    // If it is not a part of the current page (for example: dialogs), execute the namespace separately.
    const relatedNamespaces = findRelatedNamespaces(elements, currentPageId);
    const namespacesToStart = relatedNamespaces.includes(currentNs)
      ? relatedNamespaces
      : [currentNs];

    // BL-6565: Make sure that if there are connection chains that were not handled on the current page
    // (due to current namespace being different, for example when dialog is opened), those connections are also executed.
    // This necessary to prevent a situation where a dialog does an update to a dataset used on the parent page but
    // the workflow using that dataset doesn't re-run due to namespace being that of a dialog.
    if (!namespacesToStart.includes(currentPageId)) {
      namespacesToStart.push(currentPageId);
    }
    return { namespacesToStart, relatedNamespaces };
  }

  /**
   * Creates a list of chains that are next in line to start.
   * @param chains - List of all chains in the format [previousChains, currentChains].
   *   previous chains is a list chains before the store was updated (connection completed, new connection to execute)
   *   current chain is a list of chains after the store was updated.
   * @param namespacesToStart - a list of namespaces for which chains should be started. As a rule, we don't start
   *   chains/execute workflows in namespaces which are not visible on the screen.
   * @param relatedNamespaces - a list of namespaces related to the current namespace. needed for further processing
   * @returns
   *   prevChains: ConnectionChain[] - previous list of chains in the gien namespace
   *   currChains: ConnectionChain[] - current list of chains in the given namespace
   *   ns: string                    - namespace to which the namespace belongs
   *   relatedNamespaces: string[]   - namespaces related to the chain namespace
   */
  private getChainsToStart(
    chains: [Record<string, ConnectionChain[]>, Record<string, ConnectionChain[]>],
    namespacesToStart: string[],
    relatedNamespaces: string[],
  ): {
    prevChains: ConnectionChain[];
    currChains: ConnectionChain[];
    ns: string;
    relatedNamespaces: string[];
  }[] {
    const prevChainsDict = chains[0];
    const currChainsDict = chains[1];
    return namespacesToStart.map((namespace) => {
      const prevChains: ConnectionChain[] = prevChainsDict[namespace] || [];
      const currChains: ConnectionChain[] = currChainsDict[namespace] || [];
      return { prevChains, currChains, ns: namespace, relatedNamespaces };
    });
  }

  /**
   * Creates a batch of connections which need to be started.
   */
  private getConnectionsToStart(
    prevChains: ConnectionChain[],
    currChains: ConnectionChain[],
  ): Connection[] {
    const chainsToStart: ConnectionChain[] = [];
    for (const currChain of currChains) {
      // Find previous chain corresponding to the current chain.
      const prevChain = getChainByChainId(prevChains, getChainId(currChain));
      if (
        // There was nothing in the queue and new items were added to it
        (!prevChain?.length && !!currChain.length) ||
        // Item was removed from the queue. Handle new item from the queue if there is one
        (!!currChain?.length && currChain?.length < prevChain?.length) ||
        // Execute connections which were queued before runtime was started.
        this.runtimeStarted
      ) {
        chainsToStart.push(currChain);
      }
    }
    // Add first connection from each chain to run
    const connectionsToStart: Connection[] = [];
    chainsToStart.forEach((chain) => {
      connectionsToStart.push(chain[0]);
    });
    return connectionsToStart;
  }

  /**
   * Returns a row from Accounts table which should be used as a CurrentAccount variable.
   */
  private getExternalAccountToSet(
    projectId: string,
    accounts: DatasetRow[],
    currSetAccount: VarAccount | undefined,
    hasAccountSelector: boolean,
  ): VarAccount | undefined {
    // We're previewing a different user - use currently set VarAccount
    if (this.projectContextFacade.isPreviewOfAnotherUser) {
      // User changed VarAccount before the logic was able to complete. Update VarAccount to the set preview user.
      if (currSetAccount?._id !== this.projectContextFacade.varAccount?._id) {
        currSetAccount = this.projectContextFacade.varAccount;
      }
      return currSetAccount;
    }
    if (!hasAccountSelector && this.projectContextFacade.userInfo?.isExternal) {
      // In case of an external user, default to the first account from the list of accounts (server guarantees
      // that it returns only the account for that external user)
      if (accounts[0] && isVarAccount(accounts[0])) {
        return accounts[0];
      } else if (accounts[0] && !isVarAccount(accounts[0])) {
        sentryLog(`Account found but it cannot be used as a VarAccount`, 'error', {
          arguments: [accounts[0]],
        });
        return DEFAULT_ACCOUNT_VALUE;
      }
      return DEFAULT_ACCOUNT_VALUE;
    } else if (!hasAccountSelector) {
      return undefined;
    }
    // We're previewing logged in user - find a default account
    const lastSelectedAccount = this.localStorageService.getForProject<VarAccount>(
      projectId,
      StorageKey.LastExternalAccount,
    );
    const preselectAccount = currSetAccount?.Name ? currSetAccount : lastSelectedAccount;
    const preselect = accounts.find(
      (accountRow) => accountRow?.['Name'] === preselectAccount?.Name,
    );
    const selectedAccount = preselect || accounts[0];
    return selectedAccount as VarAccount;
  }

  /**
   * Based on the lists of previous and current workflows, determines which workflows are new or changed. It returns
   * a list of IDs of those workflows.
   * @param prevWorkflows - Previous list of workflows
   * @param currWorkflows - Newest list of workflows
   * @returns - List of workflow IDs
   */
  private getNewOrEditedWorkflowIds(
    prevWorkflows: Workflow[],
    currWorkflows: Workflow[],
  ): string[] {
    const workflowIds: string[] = [];
    currWorkflows
      .filter(({ isDraft }) => !isDraft)
      .forEach((currWorkflow, index) => {
        const prevWorkflow = prevWorkflows[index];
        if (
          // New workflow
          (currWorkflow?.id && !prevWorkflow) ||
          // Edited workflow
          (currWorkflow?.id &&
            currWorkflow?.id === prevWorkflow?.id &&
            !isDeepEqual(prevWorkflow, currWorkflow))
        ) {
          workflowIds.push(currWorkflow.id);
        }
      });
    return workflowIds;
  }

  /**
   * Finds and returns a list of workflows affected by the datset changes. This includes a list of workflows which
   * don't use the given dataset directly but have links to other datasets which could've been affected by the change
   * (for example if affected dataset A links to a another dataset B, and that dataset B is used by a workflow)
   * @param datasetId - dataset in which changes occured.
   * @returns - List of affected workflows.
   */
  private getWorkflowsToRefreshOnDatasetChanges(datasetId: string): Workflow[] {
    // Deduplicate by workflow id: the directly-affected workflows and those reached via linked datasets can overlap.
    const workflowsById = new Map<string, Workflow>();
    const collect = (workflow: Workflow): void => {
      if (!workflowsById.has(workflow.id)) {
        workflowsById.set(workflow.id, workflow);
      }
    };

    this.runtimeService.getWorkflowsToRefreshAfterDatasetChange(datasetId).forEach(collect);

    // Find all datasets which link to the updated dataset and then find workflows which use those datasets.
    const linkedTableIds = this.runtimeService.getTableIdsWithLinksToDataset(datasetId);
    for (const tableId of linkedTableIds) {
      this.runtimeService.getWorkflowsToRefreshAfterDatasetChange(tableId).forEach(collect);
    }

    return Array.from(workflowsById.values());
  }

  /**
   * Initializes external account variable (if any).
   */
  private initExternalAccountVar(
    projectId: string,
    accounts: DatasetRow[],
    currSetAccount: VarAccount | undefined,
    hasAccountSelector: boolean,
  ): void {
    // We can't user projectContextFacade here because it's not populated with proper data when this function is called
    combineLatest({
      accountsSchema: this.schemaService.obtainAccountsSchema(projectId, true),
      userInfo: this.projectContextFacade.userInfo$,
      varUser: this.projectContextFacade.varUser$,
    })
      .pipe(
        first(),
        map(({ accountsSchema }) => {
          this.store.dispatch(appStateActions.setExternalAccounts({ accounts }));
          const varAccount = this.getExternalAccountToSet(
            projectId,
            accounts,
            currSetAccount,
            hasAccountSelector,
          );
          return { accountsSchema, hasAccountSelector, varAccount };
        }),
      )
      .subscribe(({ accountsSchema, hasAccountSelector, varAccount }) => {
        if (
          !hasAccountSelector &&
          !this.projectContextFacade.userInfo?.isExternal &&
          !this.projectContextFacade.isPreviewOfAnotherUser
        ) {
          this.store.dispatch(
            appStateActions.updateVarAccount({ varAccount: DEFAULT_ACCOUNT_VALUE, accountsSchema }),
          );
        }
        // Update value stored in the localStorage.
        if (accounts.length && varAccount) {
          // We dispatch localStorage change action here so that intellisense context properly updates.
          // If this will start causing issues, the alternative would be to not dispatch the general localStorage action
          // but trigger context update manually after the variable is set.
          this.store.dispatch(appStateActions.updateVarAccount({ varAccount, accountsSchema }));
          this.localStorageService.setForProject(
            projectId,
            StorageKey.LastExternalAccount,
            varAccount,
          );
        }
      });
  }

  /**
   * When an accounts table is updated by a workflow, it has to be re-fetched even if the values have already been
   * fetched once. This is because a workflow on a different page could've modified the dataset and redirected
   * to a new page (BL-5839).
   * In such a case, VarAccount and externalAccounts don't have enough time to properly update and are overwritten
   * by exisitng accounts returned above via projectContextSelectors.selectExternalAccounts.
   */
  private refreshExternalAccounts(accounts: DatasetRow[] | undefined): Observable<{
    externalAccounts: DatasetRow[];
    currentVarAccount: VarAccount;
  }> {
    let extAccounts$: Observable<DatasetRow[]>;
    if (this.accountsTableStaleness.isStale) {
      // fetch rows of the Accounts table
      extAccounts$ = this.schemaService.accountsSchema?.id
        ? this.datasetRowsService.get({
            datasetId: this.schemaService.accountsSchema.id,
            includeRowId: false,
          })
        : of(accounts || []);
    } else {
      // use existing Accounts table rows
      extAccounts$ = of(accounts || []);
    }
    return extAccounts$.pipe(
      map((extAccounts) => {
        let currentVarAccount = this.projectContextFacade.varAccount ?? {
          ...DEFAULT_ACCOUNT_VALUE,
        };
        // Since we refreshed the list of accounts, the matching VarAccount value from the list is most recent
        // than the one we have in store (see comment above). If not found it the list, we need to leave it as is.
        if (this.accountsTableStaleness.isStale) {
          currentVarAccount = (extAccounts.find((acc) => acc._id === currentVarAccount?._id) ||
            currentVarAccount) as VarAccount;
        }
        this.accountsTableStaleness.markStale(false);
        return {
          externalAccounts: extAccounts,
          currentVarAccount,
        };
      }),
    );
  }

  /**
   * Show loader on appropriate UI elements which are the target of the run workflow.
   */
  private showLoadingIndicatorOnConnectedElements(
    connections: Connection[],
    workflowId: string,
  ): Action[] {
    const markPendingUiActions: Action[] = [];
    connections.forEach((connection) => {
      const elementIds: CanvasElementId[] = [];
      if (connection.target.layerType === 'Ui') {
        elementIds.push(connection.target.id);
        if (isActionWorkflow(connection, workflowId)) {
          elementIds.push(connection.source.id); // row actions
          markPendingUiActions.push(
            runtimeActions.markPendingUi({
              elementIds,
              changePending: { cause: 'row-action', workflowId },
            }),
          );
        } else {
          markPendingUiActions.push(
            runtimeActions.markPendingUi({
              elementIds,
              changePending: { cause: 'workflow', workflowId },
            }),
          );
        }
      }
    });
    return markPendingUiActions;
  }

  /**
   * Keep track of whether runtime was just started in order to process items that were added to the queue
   * before they were able to be executed. This will be set to false when runtime was already started previously
   * and set back to undefined when runtime is stopped.
   */
  private updateIsRuntimeStarted(isRunning: boolean | undefined): void {
    if (isRunning === true) {
      this.runtimeStarted = this.runtimeStarted === undefined;
      return;
    }
    this.runtimeStarted = undefined;
  }
}
